import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { browserHistory } from "react-router";
import EnrollmentGeneralSettings from "./EnrollmentGeneralSettings";
import EnrollmentPricing from "./EnrollmentPricing";
import EnrollmentTours from "./EnrollmentTours";
import Loader from "../../../../common/Loader";
import ProgramBreadcrumbsHeader from "../../../../common/ProgramBreadcrumbsHeader";
import { Unauthorized } from "../../../../../lib/coc-common-components";

import TripEventsApi from "../../../../../services/resources/TripEventsApi";
import AuthService, {
  PermissionClaims,
} from "../../../../../services/AuthService";
import AuthClient from "../../../../../services/resources/AuthClient";
import axios from "axios";
import { ApiCallErrorMessageHandler } from "../../../../../lib/coc-common-scripts";
import { getFormattedValuesForForm } from "../../../../../lib";

function ShliachEnrollmentPage(props) {
  const {
    location: {
      pathname,
      query: { tab },
    },
    params: { enrollmentId, scheduleId: paramsScheduleId },
    routeParams: { scheduleId: routeParamsScheduleId },
  } = props;

  const [authorized, setAuthorized] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [tripEvent, setTripEvent] = useState(null);
  const [shliachEnrollment, setShliachEnrollment] = useState(null);
  const [tabIndex, setTabIndex] = useState(null);

  const tabs = useMemo(
    () => [
      { name: "General", id: "general", component: EnrollmentGeneralSettings },
      { name: "Pricing", id: "pricing", component: EnrollmentPricing },
      { name: "Tours", id: "tours", component: EnrollmentTours },
    ],
    [],
  );

  const apiSignalRef = useRef(axios.CancelToken.source());

  useEffect(() => {
    const _apiSignal = apiSignalRef.current;
    return () => _apiSignal.cancel();
  }, []);

  const getTripAndEnrollmentDetails = useCallback(async () => {
    try {
      setLoading(true);

      const [tripEventResult, shliachEnrollmentResult] = await Promise.all([
        TripEventsApi.getTripEvent(
          apiSignalRef.current.token,
          paramsScheduleId,
        ),
        TripEventsApi.getTripEnrollment(
          apiSignalRef.current.token,
          enrollmentId,
        ),
      ]);

      //check that enrollment matches this event, in order to avoid issues if user manually enters other event enrollment id into url
      if (tripEventResult.id !== shliachEnrollmentResult.tripEventID) {
        setErrorMessage(
          "Invalid Enrollment request.  This Shliach Enrollment does not match this event.",
        );
        setLoading(false);
        return;
      }

      const { data: shliachCampusesData } = await AuthClient.get(
        `/Campuses/basic?shliachId=${shliachEnrollmentResult.enrolledByShliachID}`,
      ).catch((err) => {
        shliachEnrollmentResult.shliachCampuses =
          shliachEnrollmentResult.campuses; //default campuses to selected campuses from enrollment details
      });
      if (shliachCampusesData) {
        shliachEnrollmentResult.shliachCampuses = shliachCampusesData;
      }

      setShliachEnrollment(getFormattedValuesForForm(shliachEnrollmentResult));
      setTripEvent(tripEventResult);
      // don't turn off the loading state until all the values are updated in state
      setLoading(false);
    } catch (err) {
      if (!axios.isCancel(err)) {
        setErrorMessage(
          ApiCallErrorMessageHandler(
            err,
            "Sorry, something went wrong and we could not retrieve Shliach Enrollment settings. Please try again.",
          ),
        );
        setLoading(false);
      }
    }
  }, [enrollmentId, paramsScheduleId]);

  const toTab = useCallback(
    (index) => {
      if (tabIndex !== index) {
        setTabIndex(index);
        browserHistory.replace(
          `${pathname}?tab=${encodeURIComponent(tabs[index].id)}`,
        );
      }
    },
    [tabIndex, pathname, tabs],
  );

  useEffect(() => {
    if (
      !AuthService.UserHasClaim(PermissionClaims.TripDataView)
      // && !AuthService.UserHasClaim(PermissionClaims.TripChabadHouseView)  //implementation previously included allowing page access for users with TripChabadHouseView permission - don't see what role this was used for and t/f removing for now.  will re-insert if necessary.
    ) {
      setAuthorized(false);
    }

    const currentTabIndex = tabs
      .map((tab) => tab.id)
      .indexOf(decodeURIComponent(tab));
    toTab(currentTabIndex >= 0 ? currentTabIndex : 0);

    getTripAndEnrollmentDetails();
    // this effect should only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!authorized) {
    return <Unauthorized userName={AuthService.getCurrentUser().name} />;
  }

  return (
    <div className="page">
      {loading ? (
        <div className="full-page-loader">
          <Loader />
        </div>
      ) : errorMessage || !shliachEnrollment ? (
        <div className="full-page-error-text error-text">
          <img src="/img/error.svg" alt="error robot" height="240" />
          <p>
            {errorMessage ||
              "Sorry, something went wrong and we could not retrieve Shliach Enrollment settings. Please try again."}
          </p>
        </div>
      ) : (
        <React.Fragment>
          <div className="trip-page-subheader trip-page-settings-subheader">
            <div className="container flex flex-justify-space flex-align-center">
              <p onClick={() => browserHistory.goBack()} className="mr-24">
                <i className="material-icons link-text">arrow_back</i>
              </p>
              <ul className="flex">
                {tabs.map((tab, index) => (
                  <li
                    key={index}
                    className={tabIndex === index ? "active" : ""}
                    onClick={() => toTab(index)}
                  >
                    {tab.name}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="container">
            <ProgramBreadcrumbsHeader
              getPrevPages={(sched) => [
                {
                  path: `/trips/${sched.scheduleID}?tab=shluchim`,
                  title: sched.scheduleName,
                },
              ]}
              pageTitle="Shliach Enrollment"
              scheduleId={routeParamsScheduleId}
            />
            <div className="card mb-32 trip-shliach-enrollment-form">
              {React.createElement(tabs[tabIndex].component, {
                enrollment: shliachEnrollment,
                tripEvent,
              })}
            </div>
          </div>
        </React.Fragment>
      )}
    </div>
  );
}

export default memo(ShliachEnrollmentPage);
